import {
  answerChoicesFor, probeRoll, type AnswerRefAlias, type FormulaVariable,
} from '@agavi/blocks';

import type { Block, Task } from '@/types/experience';

/**
 * WHAT A RECALL CHIP IN AN OPTION COULD SHOW, so a collision between two options can be swept.
 *
 * The old rule was "a reference may be a TERM; it may never be an OPTION". The owner retired it
 * — the same hazard is already reachable with variables, an MCQ with a static `35` against one
 * reading `{random}` is sometimes two-correct, and the system warns rather than refusing. A
 * recall gets the same treatment, which means the warning has to exist.
 *
 * ## Only where the source ENUMERATES
 *
 * MCQ, Dropdown and Scale have a finite, listable set of answers. Keyword, FITB and the numeric
 * blocks do not — a student may type anything — so a collision through one of those cannot be
 * detected at all, and this returns nothing for them rather than guessing a sample. Partial
 * coverage, stated rather than hidden: the scope costed it that way.
 *
 * ## Through `answerChoicesFor`, which is the chip's own enumeration
 *
 * Not a second list of "what an MCQ can be answered with". That function is what the recall
 * test bench cycles through, so the values swept here are the values a teacher can see the chip
 * take — and it already resolves a variable chip inside an option's text against the same
 * variables, which a hand-rolled version would have shown as `{n}` and never matched.
 */
const ENUMERABLE = new Set(['mcq', 'dropdown', 'scale']);

export function recallOptionSpaces(
  /** The option texts to scan — only aliases actually cited are worth resolving. */
  optionTexts: string[],
  scope: {
    tasks: Task[];
    aliases: AnswerRefAlias[];
  } | null | undefined,
  variables: FormulaVariable[],
): Record<string, string[]> {
  if (!scope) return {};
  const cited = new Set<string>();
  for (const text of optionTexts) {
    for (const m of text.matchAll(/\bdata-answer-ref-alias="([^"]*)"/g)) cited.add(m[1] ?? '');
  }
  if (cited.size === 0) return {};

  /*
   * A PROBE ROLL, not the full domain of the source's own chips.
   *
   * A source option can itself embed a variable, so its displayed text varies too — sweeping
   * THAT as well is a product of two blocks' domains for a warning that is partial by design.
   * The probe is the same deterministic value the scenario preview and the idle card show, so
   * the swept values are ones a teacher can actually see the chip take.
   */
  const rolled = Object.fromEntries(
    Object.entries(probeRoll(variables)).map(([k, v]) => [k, Number(v)]));

  const out: Record<string, string[]> = {};
  for (const aliasId of cited) {
    const alias = scope.aliases.find(a => a.id === aliasId);
    /*
     * THE FIRST MEMBER THAT RESOLVES, which is what the sentence itself shows — a multi-member
     * alias is the same answer reached by different routes, so any of them names it. Sweeping
     * the union of every member's space would invent collisions between answers no single
     * student could ever have given.
     */
    const member = alias?.members.find(m =>
      scope.tasks.find(tk => tk.id === m.taskId)?.blocks?.some((b: Block) => b.id === m.blockId));
    if (!member) continue;
    const block = scope.tasks.find(tk => tk.id === member.taskId)
      ?.blocks?.find((b: Block) => b.id === member.blockId);
    if (!block || !ENUMERABLE.has(block.type)) continue;

    const choices = answerChoicesFor(
      block.type, (block.data ?? {}) as Record<string, unknown>,
      { variables: rolled, facet: member.facet });
    const values = choices.map(c => c.label).filter((l): l is string => !!l);
    /* Plus the empty string: a student who was never asked, or skipped it, sees nothing where
       the chip is — and two options that differ ONLY by a recall collide for exactly them. */
    if (values.length > 0) out[aliasId] = [...new Set([...values, ''])];
  }
  return out;
}

/**
 * A STABLE KEY for the map above, for an issue cache that must notice the source changing.
 *
 * The map is rebuilt every render, so its identity can never be a cache key — passing the
 * object itself would recompute every block's issues on every render, and leaving it out
 * entirely would hold a stale warning after the teacher edited the OTHER task's options. A
 * string of the contents is the only thing that is both stable and sensitive.
 */
export const recallSpacesKey = (spaces: Record<string, string[]>): string =>
  Object.keys(spaces).sort().map(k => `${k}:${spaces[k]!.join('\u0001')}`).join('\u0000');
